import React, { useState, useEffect } from 'react';
import config from '../config';
import ConfirmDialog from './ConfirmDialog';
import '../styles/usuarios.css';

const GestionUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [usuarioEditar, setUsuarioEditar] = useState(null);
  const [usuarioAEliminar, setUsuarioAEliminar] = useState(null);
  const [mostrarConfirmEliminar, setMostrarConfirmEliminar] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'profesional'
  });

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem(config.tokenKey);
      const response = await fetch(`${config.apiUrl}/usuarios`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar usuarios');
      }

      const data = await response.json();
      setUsuarios(data);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem(config.tokenKey);
      
      if (usuarioEditar) {
        // Actualizar usuario
        const response = await fetch(`${config.apiUrl}/usuarios/${usuarioEditar.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            nombre: formData.nombre,
            email: formData.email,
            rol: formData.rol
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Error al actualizar usuario');
        }

        alert('Usuario actualizado correctamente');
      } else {
        // Crear nuevo usuario
        if (!formData.password) {
          alert('La contraseña es requerida');
          return;
        }

        const response = await fetch(`${config.apiUrl}/usuarios`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Error al crear usuario');
        }

        alert('Usuario creado correctamente');
      }

      cerrarModal();
      cargarUsuarios();
    } catch (error) {
      console.error('Error:', error);
      alert(error.message);
    }
  };

  const handleEliminar = (usuario) => {
    setUsuarioAEliminar(usuario);
    setMostrarConfirmEliminar(true);
  };

  const confirmarEliminar = async () => {
    try {
      const token = localStorage.getItem(config.tokenKey);
      const response = await fetch(`${config.apiUrl}/usuarios/${usuarioAEliminar.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error al eliminar usuario');
      }

      cargarUsuarios();
      setMostrarConfirmEliminar(false);
      setUsuarioAEliminar(null);
    } catch (error) {
      console.error('Error:', error);
      alert(error.message);
    }
  };

  const cancelarEliminar = () => {
    setMostrarConfirmEliminar(false);
    setUsuarioAEliminar(null);
  };

  const abrirModalNuevo = () => {
    setUsuarioEditar(null);
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rol: 'profesional'
    });
    setMostrarModal(true);
  };

  const abrirModalEditar = (usuario) => {
    setUsuarioEditar(usuario);
    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      password: '',
      rol: usuario.rol
    });
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setUsuarioEditar(null);
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rol: 'profesional'
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getRolBadgeClass = (rol) => {
    switch (rol) {
      case 'admin':
        return 'badge-admin';
      case 'secretaria':
        return 'badge-secretaria';
      case 'profesional':
        return 'badge-profesional';
      default:
        return 'badge-default';
    }
  };

  const getRolLabel = (rol) => {
    switch (rol) {
      case 'admin':
        return 'Administrador';
      case 'secretaria':
        return 'Secretaria';
      case 'profesional':
        return 'Profesional';
      default:
        return rol;
    }
  };

  if (loading) {
    return <div className="loading">Cargando usuarios...</div>;
  }

  return (
    <div className="usuarios-container">
      <div className="usuarios-header">
        <h1>👥 Gestión de Usuarios</h1>
        <button className="btn-primary" onClick={abrirModalNuevo}>
          ➕ Nuevo Usuario
        </button>
      </div>

      <div className="usuarios-tabla">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Fecha Creación</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map(usuario => (
              <tr key={usuario.id}>
                <td>{usuario.id}</td>
                <td>{usuario.nombre}</td>
                <td>{usuario.email}</td>
                <td>
                  <span className={`badge ${getRolBadgeClass(usuario.rol)}`}>
                    {getRolLabel(usuario.rol)}
                  </span>
                </td>
                <td>{new Date(usuario.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="acciones-buttons">
                    <button
                      className="btn-edit"
                      onClick={() => abrirModalEditar(usuario)}
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleEliminar(usuario)}
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {usuarios.length === 0 && (
          <div className="no-data">No hay usuarios registrados</div>
        )}
      </div>

      {/* Modal de crear/editar */}
      {mostrarModal && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-contenido" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{usuarioEditar ? '✏️ Editar Usuario' : '➕ Nuevo Usuario'}</h2>
              <button className="btn-close" onClick={cerrarModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nombre completo *</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    placeholder="Ej: María García"
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="usuario@ejemplo.com"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Contraseña {usuarioEditar ? '(dejar vacío para no cambiar)' : '*'}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!usuarioEditar}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                  />
                </div>

                <div className="form-group">
                  <label>Rol *</label>
                  <select
                    name="rol"
                    value={formData.rol}
                    onChange={handleChange}
                    required
                  >
                    <option value="profesional">Profesional</option>
                    <option value="secretaria">Secretaria</option>
                    <option value="admin">Administrador</option>
                  </select>
                  <small>
                    {formData.rol === 'admin' && '⚠️ Acceso total al sistema'}
                    {formData.rol === 'secretaria' && '📋 Gestión de pacientes, turnos y pagos'}
                    {formData.rol === 'profesional' && '👨‍⚕️ Solo sus turnos y pacientes'}
                  </small>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={cerrarModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {usuarioEditar ? 'Actualizar' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      <ConfirmDialog
        isOpen={mostrarConfirmEliminar}
        title="Eliminar Usuario"
        message={`¿Estás seguro de que deseas eliminar al usuario "${usuarioAEliminar?.nombre}"?`}
        detail={`Email: ${usuarioAEliminar?.email || 'N/A'} | Rol: ${usuarioAEliminar?.rol || 'N/A'}`}
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
        onConfirm={confirmarEliminar}
        onCancel={cancelarEliminar}
        isDangerous={true}
      />
    </div>
  );
};

export default GestionUsuarios;
